import React, { FC, createRef, ReactElement } from 'react';
import classnames from 'classnames';
import Downshift, { StateChangeOptions } from 'downshift';
import {
  Asset,
  ArrowDownwardIcon,
  Loading,
  CloseIcon,
} from '@clearcover/atlas';
import { isPresent } from 'Shared/utils/object';

import { SearchInput } from '../Search';
import { TextInputProps, Input, Label } from '../Text';
import styles from './styles.module.css';

export type BaseProps = {
  options: { [value: string]: string | ReactElement };
  defaultValue?: string;
  value?: string | null | undefined;
  labelPosition?: 'above' | 'inline';
  loading?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  children?: React.ReactNode;
} & Omit<
  TextInputProps,
  'onChange' | 'onClick' | 'onFocus' | 'defaultValue' | 'value' | 'onSelect'
>;

export type SelectInputProps = BaseProps & {
  variant: 'select' | 'selectAutocomplete' | 'typeahead' | 'floatingTypeahead';
  onChange: (value: string) => void;
  onSelect?: (value: string) => void;
  onFocus?: () => void;
  optionsSortFn?: (a: string, b: string) => number;
};

export type ComboInputProps = BaseProps & {
  variant: 'combobox';
  onChange: (change: StateChangeOptions<string>) => void;
  onSelect?: () => void;
  onFocus?: () => void;
  optionsSortFn?: (a: string, b: string) => number;
};

export const isComboBoxType = (
  props: SelectInputProps | ComboInputProps
): props is ComboInputProps => {
  return props.variant === 'combobox';
};

export const isAutocompleteType = (
  props: SelectInputProps | ComboInputProps
): props is SelectInputProps => {
  return props.variant === 'selectAutocomplete';
};

export const isTypeaheadType = (
  props: SelectInputProps | ComboInputProps
): props is SelectInputProps => {
  return props.variant === 'typeahead';
};

export const isFloatingTypeaheadType = (
  props: SelectInputProps | ComboInputProps
): props is SelectInputProps => {
  return props.variant === 'floatingTypeahead';
};

export const NO_RESULTS = 'noResultsFound';
const INVALID_VALUE_ERROR_MESSAGE = 'Please select a valid value';

export const SelectInput: FC<SelectInputProps | ComboInputProps> = props => {
  const {
    options,
    className,
    defaultValue = '',
    value,
    label,
    labelPosition = 'above',
    loading,
    disabled,
    onSelect,
    onChange,
    onFocus,
    optionsSortFn,
    tooltip,
    tooltipPosition,
    isClearable,
    onClear,
    ...restOfInputProps
  } = props;

  const isCombobox = isComboBoxType(props);
  const isFloatingTypeahead = isFloatingTypeaheadType(props);
  const isTypeahead = isTypeaheadType(props) || isFloatingTypeahead;
  const isAutocomplete = isAutocompleteType(props) || isTypeahead;
  const isBasicSelect = !isCombobox && !isAutocomplete;
  const isValidValue = !value || props.variant !== 'select' || value in options;
  const downshiftValue = isValidValue ? value : INVALID_VALUE_ERROR_MESSAGE;

  // Combobox uses onStateChange, autocomplete uses onInputValueChange
  const hasAutocomplete = isAutocomplete ? 'onInputValueChange' : 'onChange';
  const changeHandlerKey = isCombobox ? 'onStateChange' : hasAutocomplete;
  const defaultHighlightedIndex = isAutocomplete ? 0 : null;
  const allowUserInput = isCombobox || isAutocomplete;
  const showDropdownIcon = !(isCombobox || isTypeahead);

  const inputRef = createRef<HTMLInputElement>() as React.RefObject<
    HTMLInputElement
  >;
  const InputComponent = isTypeahead ? SearchInput : Input;

  if (!restOfInputProps.errorMessage && !isValidValue) {
    restOfInputProps.errorMessage = INVALID_VALUE_ERROR_MESSAGE;
  }

  return (
    <Downshift
      {...{ [changeHandlerKey]: onChange }}
      inputValue={downshiftValue}
      selectedItem={value}
      initialInputValue={defaultValue}
      defaultHighlightedIndex={defaultHighlightedIndex}
      onSelect={onSelect && onSelect}
    >
      {({
        getLabelProps,
        getInputProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
        selectedItem,
        inputValue,
        clearSelection,
        openMenu,
        toggleMenu,
      }) => {
        const showSelectInputWithIcon =
          inputValue &&
          options[inputValue] &&
          typeof options[inputValue] !== 'string';

        const handleWrapperMouseDown = () => {
          if (
            !inputRef.current ||
            inputRef.current === document.activeElement
          ) {
            openMenu();
          } else {
            inputRef.current?.focus();
          }
        };

        return (
          <div
            className={classnames(
              styles.select,
              {
                [styles.inlineLabel]: labelPosition === 'inline',
                [styles.disabled]: disabled,
                [styles.floatingTypeahead]: isFloatingTypeahead,
                [styles.typeaheadSelect]: isTypeahead,
              },
              className
            )}
          >
            {!isTypeahead && props.label && (
              <Label
                {...getLabelProps()}
                disabled={disabled}
                tooltip={tooltip}
                tooltipPosition={tooltipPosition}
              >
                {label}
              </Label>
            )}
            <div
              className={classnames(styles.selectInput, {
                [styles.disabled]: disabled,
              })}
            >
              <div
                onMouseDown={() => {
                  if (isBasicSelect) {
                    toggleMenu();
                  } else {
                    if (isTypeahead) {
                      // Do not clear selection for Typeahead Autocomplete
                      handleWrapperMouseDown();
                    } else {
                      clearSelection(() => {
                        handleWrapperMouseDown();
                      });
                    }
                  }
                }}
              >
                {showSelectInputWithIcon ? (
                  inputValue && (
                    <div
                      data-testid={restOfInputProps.name
                        ?.toLowerCase()
                        .replace(/[^A-Za-z]/, '')}
                      className={classnames(styles.selectInputWithIcon, {
                        [styles.disabled]: disabled,
                      })}
                    >
                      {options[inputValue]}
                    </div>
                  )
                ) : (
                  <InputComponent
                    label={label}
                    floatingIcon={isFloatingTypeahead}
                    data-testid={restOfInputProps.name
                      ?.toLowerCase()
                      .replace(/[^A-Za-z]/, '')}
                    data-open-state={isOpen ? 'open' : 'closed'}
                    className={classnames({
                      [styles.showDropdownIcon]: showDropdownIcon,
                      [styles.isClearable]: isClearable,
                      [styles.typeaheadInput]: isTypeahead,
                    })}
                    {...getInputProps({
                      onFocus: () => {
                        if (isBasicSelect || isTypeahead) {
                          openMenu();
                          onFocus && onFocus();
                        } else {
                          // clear selection for combobox and selectAutocomplete
                          clearSelection(() => {
                            openMenu();
                            onFocus && onFocus();
                          });
                        }
                      },
                      onKeyDown: (
                        keyEvent: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        // prevent submit on "enter"
                        if (
                          isCombobox &&
                          (keyEvent.charCode || keyEvent.keyCode) === 13
                        ) {
                          keyEvent.preventDefault();
                        }
                        if (props.onKeyDown) {
                          props.onKeyDown(keyEvent);
                        }
                      },
                      disabled,
                      autoComplete: 'new-password',
                      ...restOfInputProps,
                      inputRef,
                      type: allowUserInput ? 'text' : 'button',
                      value:
                        isPresent(inputValue) && inputValue !== ''
                          ? options[inputValue] ?? inputValue
                          : '',
                      tooltip,
                      tooltipPosition,
                    })}
                  />
                )}
                {isClearable && onClear && !disabled && (
                  <button
                    onClick={onClear}
                    className={classnames(styles.clear, {
                      [styles.hasDropDownIcon]: showDropdownIcon,
                    })}
                  >
                    <Asset
                      icon={CloseIcon}
                      height={10}
                      width={10}
                      description="clear"
                    />
                  </button>
                )}
                <div
                  className={classnames(styles.stateIcon, {
                    [styles.disabled]: loading || disabled,
                    [styles.isOpen]: isOpen,
                    [styles.hasError]: isPresent(restOfInputProps.errorMessage),
                  })}
                >
                  {loading ? (
                    <Loading />
                  ) : showDropdownIcon ? (
                    <Asset
                      icon={ArrowDownwardIcon}
                      height={10}
                      width={10}
                      description="dropdown arrow"
                    />
                  ) : (
                    ''
                  )}
                </div>
              </div>
              <ul {...getMenuProps()}>
                {isOpen &&
                  Object.keys(options)
                    .sort(optionsSortFn ? optionsSortFn : () => 0)
                    .map((value, index) => {
                      const optionDisabled = value === NO_RESULTS;
                      return (
                        <li
                          key={`item-${index}`}
                          data-testid={value.trim()}
                          {...getItemProps({
                            index,
                            item: value,
                            disabled: optionDisabled,
                            className: classnames(styles.listItem, {
                              [styles.itemFocus]:
                                !optionDisabled && highlightedIndex === index,
                              [styles.itemSelected]:
                                !optionDisabled &&
                                selectedItem &&
                                selectedItem === value,
                            }),
                          })}
                        >
                          {options[value]}
                        </li>
                      );
                    })}
              </ul>
            </div>
          </div>
        );
      }}
    </Downshift>
  );
};
